<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'

import AppFooter from '../components/AppFooter.vue'
import LandingBackground from '../components/LandingBackground.vue'
import SimpleHeader from '../components/SimpleHeader.vue'
import { brand } from '../config/brand'
import type { LandingSeo } from '../lib/seoParser'

const props = defineProps<{
  landingSeo: LandingSeo
  canonicalPath: string
  breadcrumbName: string
  howToTitle?: string
  seoTitle?: string
  seoDescription?: string
}>()

function withBase(p: string) {
  return `${import.meta.env.BASE_URL || '/'}${String(p || '').replace(/^\//, '')}`
}

const pageTitle = computed(() => props.seoTitle || props.landingSeo.h1 || brand.productName)
const description = computed(() => props.seoDescription || '')
const siteUrl = ((import.meta.env.VITE_SITE_URL as string | undefined) || 'https://www.wuhenai.com').replace(/\/+$/, '')
const canonical = computed(() => `${siteUrl}${props.canonicalPath || '/'}`)

useHead(() => ({
  title: pageTitle.value,
  meta: description.value ? [{ name: 'description', content: description.value }] : [],
  link: [{ rel: 'canonical', href: canonical.value }],
}))
</script>

<template>
  <LandingBackground>
    <div class="page">
      <SimpleHeader :withBase="withBase" :title="props.breadcrumbName" />

      <main class="container" style="padding-top: 96px; padding-bottom: 36px">
        <section class="hero">
          <h1 style="margin: 0 0 10px; font-size: 34px; line-height: 1.2">{{ props.landingSeo.h1 || props.seoTitle }}</h1>
          <p v-if="props.seoDescription" style="margin: 0; color: rgba(255, 255, 255, 0.82)">{{ props.seoDescription }}</p>
        </section>

        <section class="miniCard" style="margin-top: 18px; padding: 16px">
          <h3 style="margin: 0 0 8px">功能开发中</h3>
          <p style="margin: 0; color: rgba(255, 255, 255, 0.82)">
            当前落地页已上线，工具能力正在接入统一工作台。你可以先前往工作台查看最新进度。
          </p>
          <div style="margin-top: 12px">
            <a class="cta loginBtn" :href="withBase('/ai-workbench/')">进入{{ brand.workbenchName }}</a>
          </div>
        </section>

        <section v-if="props.landingSeo.howto.length" style="margin-top: 18px">
          <h2>{{ props.howToTitle || '使用方法' }}</h2>
          <div class="grid2">
            <div v-for="(s, i) in props.landingSeo.howto" :key="i" class="miniCard" style="padding: 14px">
              <h4 style="margin: 0 0 6px">{{ s.title }}</h4>
              <p style="margin: 0; color: rgba(255, 255, 255, 0.82)">{{ s.desc }}</p>
            </div>
          </div>
        </section>

        <section v-if="props.landingSeo.whereItems.length" style="margin-top: 18px">
          <h2>{{ props.landingSeo.whereTitle || '适用场景' }}</h2>
          <div class="grid3">
            <div v-for="(it, i) in props.landingSeo.whereItems" :key="i" class="miniCard" style="padding: 14px">
              <h4 style="margin: 0 0 6px">{{ it.title }}</h4>
              <p style="margin: 0; color: rgba(255, 255, 255, 0.72)">{{ it.variant }}</p>
            </div>
          </div>
        </section>

        <section v-if="props.landingSeo.whyNeedItems.length" style="margin-top: 18px">
          <h2>{{ props.landingSeo.whyNeedTitle || '为什么需要' }}</h2>
          <div class="grid2">
            <div v-for="(it, i) in props.landingSeo.whyNeedItems" :key="i" class="miniCard" style="padding: 14px">
              <h4 style="margin: 0 0 6px">{{ it.title }}</h4>
              <p style="margin: 0; color: rgba(255, 255, 255, 0.82)">{{ it.desc }}</p>
            </div>
          </div>
        </section>

        <section v-if="props.landingSeo.whyItems.length" style="margin-top: 18px">
          <h2>{{ props.landingSeo.whyTitle || '为什么选择我们' }}</h2>
          <div class="grid2">
            <div v-for="(it, i) in props.landingSeo.whyItems" :key="i" class="miniCard" style="padding: 14px">
              <h4 style="margin: 0 0 6px">{{ it.title }}</h4>
              <p style="margin: 0; color: rgba(255, 255, 255, 0.82)">{{ it.desc }}</p>
            </div>
          </div>
        </section>

        <section v-if="props.landingSeo.faq.length" style="margin-top: 18px">
          <h2>{{ props.landingSeo.faqTitle || '常见问题' }}</h2>
          <div class="grid2">
            <div v-for="(it, i) in props.landingSeo.faq" :key="i" class="miniCard" style="padding: 14px">
              <h4 style="margin: 0 0 6px">{{ it.question }}</h4>
              <p style="margin: 0; color: rgba(255, 255, 255, 0.82)">{{ it.answer }}</p>
            </div>
          </div>
        </section>
      </main>

      <AppFooter :withBase="withBase" />
    </div>
  </LandingBackground>
</template>
